import { api } from "@/lib/api";

const SW_PATH = "/sw.js";

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
  } catch {
    return null;
  }
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await api.get<{ publicKey: string }>("/push/vapid-public-key/");
    return data.publicKey?.trim() || null;
  } catch {
    return null;
  }
}

function subscriptionToJSON(subscription: PushSubscription) {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint || subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh || "",
      auth: json.keys?.auth || "",
    },
  };
}

/** Registra SW + PushManager e envia a inscrição ao backend. */
export async function subscribeWebPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  if (Notification.permission !== "granted") return false;

  const registration = await registerPushServiceWorker();
  if (!registration) return false;

  await navigator.serviceWorker.ready;

  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) return false;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  const payload = subscriptionToJSON(subscription);
  if (!payload.keys.p256dh || !payload.keys.auth) return false;

  try {
    await api.post("/push/subscribe/", payload);
    return true;
  } catch {
    return false;
  }
}

/** Cancela inscrição no browser e marca inativa no backend. */
export async function unsubscribeWebPush(): Promise<void> {
  if (!isPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    try {
      await api.post("/push/unsubscribe/", { endpoint });
    } catch {
      // Best-effort — inscrição local já foi removida.
    }
  } catch {
    // Ignore failures while turning notifications off.
  }
}
